import { ref } from "vue";
import axios from "axios";
import { useCookie } from "#app";
import globalConfig from "~/composables/globalConfig";

/**
 * Data structure for SZMD box rating response data.
 */
export interface SzmdRatingData {
	company: {
		id: number;
		name: string;
		primary_lang: string;
		boxes: Array<{
			free_type: string;
			data: Record<
				string,
				{
					images?: Array<{
						cloudinary: string;
					}>;
					boxdescription?: Array<string | null>;
				}
			>;
		}>;
		// TODO - Logóhoz kellett, de ha nem használjuk akkor töröljük
		metas: Array<{
			key: string;
			value: {
				images: Array<{
					cloudinary: string;
				}>;
			};
		}>;
	};
	user: {
		remaining_votes: number;
		total_votes: number;
		max_votes: number;
	};
	hasMoreCompanies: boolean;
}

/**
 * Data structure for free box images with descriptions.
 */
type FreeBoxImages = Array<{
	cloudinary: string | null;
	description?: Record<string, string | null>;
}>;

/**
 * Data structure for rated company vote post request.
 */
export interface RatedCompany {
	company_id: number;
	rate: number;
}

/**
 * States for SZMD rating process.
 */
export type State =
	| "loading"
	| "voting"
	| "voting_not_available"
	| "error"
	| "cannot_rate_this_company"
	| "already_rated_to_company_this_week"
	| "no_remaining_votes"
	| "no_authentication";

/**
 * State management for SZMD box rating data.
 */
export class SzmdRatingState {
	private _state = ref<State>("loading");
	private _ratingData = ref<SzmdRatingData | null>(null);
	private _ratedData = ref<Record<number, number>>({});

	get state() {
		return this._state.value;
	}

	get ratingData() {
		return this._ratingData.value;
	}

	get ratedData() {
		return this._ratedData.value;
	}

	get logoFileName() {
		return (
			this._ratingData.value?.company.metas.find((meta) => meta.key === "logo")?.value?.images?.[0]?.cloudinary ||
			null
		);
	}

	setState(state: State) {
		this._state.value = state;
	}

	setRatingData(data: SzmdRatingData | null) {
		this._ratingData.value = data;
	}

	setRatedData(data: Record<number, number>) {
		this._ratedData.value = data;
	}

	clearRatings() {
		this._ratedData.value = {};
	}

	getBoxesByFreeType(freeType: string) {
		return this._ratingData.value?.company.boxes.filter((box) => box.free_type === freeType) || [];
	}

	/**
	 * Get box images by box index, returns an array of image URLs and descriptions.
	 * @param boxKey index of the box in the boxes array
	 */
	getFreeBoxImages(boxKey: number): FreeBoxImages {
		const box = this._ratingData.value?.company.boxes[boxKey];

		const returnValue: FreeBoxImages = [];

		if (!box) {
			return returnValue;
		}

		// Search images in all languages
		const languages = box.data;

		const images = languages["en"].images || [];
		const boxDescriptions = languages["en"].boxdescription || [];

		// Add all images with description if available
		boxDescriptions.forEach((boxDescription, index) => {
			const description: Record<string, string | null> = {};
			// Find description for the all languages
			Object.entries(box.data).forEach(([langKey, item]) => {
				description[langKey] = item.boxdescription?.[index] || "";
			});

			// Find the first filled description and set it as the defaultDescription variable value
			const defaultDescription = Object.values(description).find((desc) => desc) || "";

			// Set the description value for all languages where it's not specified
			Object.keys(description).forEach((lang) => {
				if (!description[lang] || description[lang] === "") {
					description[lang] = defaultDescription;
				}
			});

			returnValue.push({
				cloudinary: images?.[index]?.cloudinary,
				description: description,
			});
		});

		return returnValue;
	}

	/**
	 * Get the total count of images for boxes with the specified free_type.
	 * @param freeType the free_type to filter boxes by
	 * @returns total number of images across all matching boxes
	 */
	getImageCountByFreeType(freeType: string): number {
		const boxes = this.getBoxesByFreeType(freeType);

		let totalImageCount = 0;

		boxes.forEach((box) => {
			totalImageCount += box?.data?.["en"]?.boxdescription?.length || 0;
		});

		return totalImageCount;
	}
}

/**
 * Service to handle fetching and submitting SZMD boxrating data.
 */
export class SzmdRatingService {
	private apiUrl: string;
	private state: SzmdRatingState;

	constructor(state: SzmdRatingState) {
		this.apiUrl = globalConfig.API;
		this.state = state;
	}

	/**
	 * Get authentication token from cookies.
	 */
	private _getToken(): string | null {
		const token = useCookie("token").value;
		if (!token) {
			const error: Error & { errorCode?: string } = new Error("No authentication token found");
			error.errorCode = "no_authentication";
			throw error;
		}
		return token;
	}

	/**
	 * Fetch rating data for random or a specific company.
	 */
	async fetchRatingData(companySlug?: string | null): Promise<void> {
		try {
			this.state.setState("loading");
			const companyId = this.state.ratingData?.company.id;
			const ignoreQuery = companyId ? `?ignore_company_id=${companyId}` : "";

			// Check slug, if provided, otherwise fetch random company
			const url =
				companySlug && companySlug.trim() !== ""
					? `${this.apiUrl}/szmd/szmd-box-vote/${encodeURIComponent(companySlug)}${ignoreQuery}`
					: `${this.apiUrl}/szmd/szmd-box-vote${ignoreQuery}`;

			const response = await axios({
				method: "get",
				url: url,
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${this._getToken()}`,
				},
			});

			// Check this company is rateable
			if (
				response.data.company.length === 0 &&
				companySlug &&
				companySlug.trim() !== "" &&
				response.data.user.remaining_votes > 0
			) {
				this.state.setState("cannot_rate_this_company");
				return;
			}

			// Check if no more companies available
			if (response.data.company.length === 0 || response.data.user.remaining_votes == 0) {
				this.state.setState("already_rated_to_company_this_week");
				return;
			}

			this.state.setRatingData(response.data);

			this.state.setState("voting");
		} catch (error) {
			this._handleError(error);
		}
	}

	/**
	 * Submit a box rating for the current company and fetch the next one.
	 */
	async submitRatings(rate: number): Promise<void> {
		this.state.setState("loading");

		if (!this.state.ratingData?.company.id) {
			console.error("No company to rate.");
			this.state.setState("error");
			return;
		}

		try {
			// Submit the rating for the current company
			await axios.post(
				`${this.apiUrl}/szmd/szmd-box-vote`,
				{
					company_id: this.state.ratingData.company.id,
					rate: rate,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${this._getToken()}`,
						Accept: "application/json",
					},
					withCredentials: true,
				}
			);

			this.state.clearRatings();
		} catch (error) {
			this._handleError(error);
		}

		// Fetch the next company to rate
		this.fetchRatingData();
	}

	private _handleError(error: unknown): void {
		const err = error as { response?: { data?: { error?: string }; status?: number }; message?: string };
		if (err.response?.data?.error === "voting_not_available") {
			this.state.setState("voting_not_available");
		} else if (err.response?.status === 401) {
			this.state.setState("no_authentication");
		} else if (err.response?.data?.error === "already_rated_to_company_this_week") {
			this.state.setState("already_rated_to_company_this_week");
		} else if (err.response?.data?.error === "no_remaining_votes") {
			this.state.setState("already_rated_to_company_this_week");
		} else {
			this.state.setState("error");
			console.error("An unexpected error occurred: " + (err.message || "Unknown error"), {
				errorCode: err.response?.data?.error,
				status: err.response?.status,
			});
		}

		console.error("Error fetching SZMD rating data:", error);
	}
}
